import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { CustomerService } from "../../services/CustomerService";
import { PostApiKeyDto } from "../../services/Dtos/ApiKeyDtos";

// Types
export interface GetApiKeyDto {
  id: number;
  name: string;
  key: string;
  phone: string | null;
  email: string | null;
  description: string | null;
}

export interface ApiKeySidebarProps {
  apiKeyId?: number;
  customerId: number;
  onClose: () => void;
  onSave: () => void;
}

const generateSuggestedApiKey = (): string => {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

  const generatePart = () =>
    Array(5)
      .fill(0)
      .map(() =>
        characters.charAt(Math.floor(Math.random() * characters.length))
      )
      .join("");

  const part1 = generatePart();
  const part2 = generatePart();
  const part3 = generatePart();

  return `${part1}-${part2}-${part3}`;
};

const isValidApiKeyFormat = (key: string): boolean => {
  const apiKeyRegex = /^.{5}-.{5}-.{5}$/;
  return apiKeyRegex.test(key);
};

const isValidUSPhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^(?:\(\d{3}\)\s?\d{3}-\d{4}|\d{3}-\d{3}-\d{4}|\d{10})$/;
  return phoneRegex.test(phone);
};

// Format phone number to (XXX) XXX-XXXX
const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length !== 10) return phone;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

const ApiKeySidebar: React.FC<ApiKeySidebarProps> = ({
  apiKeyId,
  customerId,
  onClose,
  onSave,
}) => {
  // Initialize all fields as empty strings to enforce string type
  const [formData, setFormData] = useState({
    name: "",
    key: generateSuggestedApiKey(),
    phone: "",
    email: "",
    description: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load existing API key data when editing
  useEffect(() => {
    if (apiKeyId) {
      setIsLoading(true);
      CustomerService.apiKey
        .getApiKeyById(apiKeyId)
        .then((response) => {
          const { name, key, phone, email, description } = response.data;
          setFormData({
            name: name,
            key: key,
            phone: phone || "",
            email: email || "",
            description: description || "",
          });
          setErrors({});
        })
        .catch((err) => {
          console.error("Error loading API key:", err);
          setErrors({ general: "Failed to load API key data" });
        })
        .finally(() => setIsLoading(false));
    } else {
      resetForm();
    }
  }, [apiKeyId]);

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      name: "",
      key: generateSuggestedApiKey(),
      phone: "",
      email: "",
      description: "",
    });
    setErrors({});
  };

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === "phone") {
      formattedValue = formatPhoneNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    // Clear error for the field when user modifies it
    if (errors[name]) {
      setErrors((prev) => {
        const { [name]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  // Validate individual fields
  const validateField = (name: string, value: string): string | null => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        return null;
      case "key":
        if (!value.trim()) return "API Key is required";
        if (!isValidApiKeyFormat(value))
          return "API Key must be in format XXXXX-XXXXX-XXXXX";
        return null;
      case "phone":
        if (!value.trim()) return "Phone is required";
        if (!isValidUSPhoneNumber(value))
          return "Please enter a valid US phone number";
        return null;
      case "email":
        if (!value.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Please enter a valid email";
        return null;
      case "description":
        if (!value.trim()) return "Description is required";
        return null;
      default:
        return null;
    }
  };

  // Validate entire form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Check all fields
    ["name", "key", "phone", "email", "description"].forEach((field) => {
      const error = validateField(
        field,
        formData[field as keyof typeof formData]
      );
      if (error) newErrors[field] = error;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return; // Stop if validation fails
    }

    setIsLoading(true);

    try {
      const payload: PostApiKeyDto = {
        name: formData.name.trim(),
        key: formData.key.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        description: formData.description.trim(),
        customerId,
      };

      if (apiKeyId) {
        await CustomerService.apiKey.updateApiKey(apiKeyId, payload);
        toast.success("API Key updated successfully");
      } else {
        await CustomerService.apiKey.createApiKey(payload);
        toast.success("API Key created successfully");
      }

      onSave();
      onClose();
      resetForm();
    } catch (err) {
      console.error("Error saving API key:", err);
      toast.error("Failed to save API key");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {isLoading ? (
        <div className="d-flex justify-content-center p-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {errors.general && (
            <div className="alert alert-danger mb-4" role="alert">
              {errors.general}
            </div>
          )}

          <div className="mb-3">
            <label htmlFor="name" className="form-label fw-bold">Name *</label>
            <input
              type="text"
              className={`form-control ${errors.name ? "is-invalid" : ""}`}
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>

          <div className="mb-3">
            <label htmlFor="key" className="form-label fw-bold">API Key *</label>
            <input
              type="text"
              className={`form-control ${errors.key ? "is-invalid" : ""}`}
              id="key"
              name="key"
              value={formData.key}
              onChange={handleInputChange}
              disabled={!!apiKeyId}
              required
            />
            {errors.key && <div className="invalid-feedback">{errors.key}</div>}
            {!apiKeyId && (
              <small className="form-text text-muted">
                Format: XXXXX-XXXXX-XXXXX (X = digits or letter)
              </small>
            )}
          </div>

          <div className="mb-3">
            <label htmlFor="phone" className="form-label fw-bold">Phone *</label>
            <input
              type="tel"
              className={`form-control ${errors.phone ? "is-invalid" : ""}`}
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="(XXX) XXX-XXXX"
              required
            />
            {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
          </div>

          <div className="mb-3">
            <label htmlFor="email" className="form-label fw-bold">Email *</label>
            <input
              type="email"
              className={`form-control ${errors.email ? "is-invalid" : ""}`}
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
          </div>

          <div className="mb-4">
            <label htmlFor="description" className="form-label fw-bold">Description *</label>
            <textarea
              className={`form-control ${errors.description ? "is-invalid" : ""}`}
              id="description"
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleInputChange}
              required
            />
            {errors.description && <div className="invalid-feedback">{errors.description}</div>}
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : apiKeyId ? "Update API Key" : "Create API Key"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ApiKeySidebar;
